/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useState, useCallback } from "react";
import {
  Box,
  CardHeader,
  CardContent,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  CircularProgress,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import CommonDataGrid from "../CodingTracker/CommonDataGrid";
import SolutionPreviewPanel from "../CodingTracker/SolutionPreviewPanel";
import { fetchSubmission, ctRecordExportActivity } from "../../services/api";
import {
  SUCCESS,
  WARNING,
  DANGER,
  PRIMARY,
  INDIGO,
} from "../../utils/colors";
import GlassCard from "../Cards/GlassCard";
import { panelMutedSurfaceSx } from "../../utils/colors";

// ── Type classifiers ──────────────────────────────────────────────────────
const SCORE_RANGES      = ["0-40", "40-60", "60-80", "80-100"];
const COMPLETION_TYPES  = ["completed", "partial", "notAttempted"];
const PLAGIARISM_TYPES  = ["clean", "suspected", "highSimilarity"];

const getCategory = (type) => {
  if (type === "student-detail")       return "detail";
  if (SCORE_RANGES.includes(type))     return "score";
  if (COMPLETION_TYPES.includes(type)) return "completion";
  if (PLAGIARISM_TYPES.includes(type)) return "plagiarism";
  return "general";
};

// ── Label / color maps ─────────────────────────────────────────────────────
const COMPLETION_LABEL = {
  completed:    "Completed",
  partial:      "Partial",
  notAttempted: "Not Attempted",
};

const PLAGIARISM_LABEL = {
  clean:           "Clean",
  suspected:       "Suspected",
  highSimilarity:  "High Similarity",
};

const COMPLETION_COLOR = {
  completed:    SUCCESS,
  partial:      WARNING,
  notAttempted: DANGER,
};

const PLAGIARISM_COLOR = {
  clean:          SUCCESS,
  suspected:      WARNING,
  highSimilarity: DANGER,
};

// ── CSV export helper ──────────────────────────────────────────────────────────────
const escapeCell = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return s.includes(",") || s.includes('"') || s.includes("\n")
    ? `"${s.replace(/"/g, '""')}"`
    : s;
};

const exportCSV = (rows, title) => {
  const headers = [
    "Student Name", "Roll Number", "Score (%)",
    "Completion Status", "Plagiarism Status", "Last Submission",
    "Section", "Department",
  ];
  const lines = [
    headers.join(","),
    ...rows.map((r) =>
      [
        escapeCell(r.name),
        escapeCell(r.rollNumber),
        escapeCell(r._score ?? r.score ?? ""),
        escapeCell(r.completionStatus ?? ""),
        escapeCell(r.plagiarismStatus ?? ""),
        escapeCell(
          r.lastSubmission
            ? new Date(r.lastSubmission).toLocaleDateString()
            : ""
        ),
        escapeCell(r.section ?? ""),
        escapeCell(r.department ?? ""),
      ].join(",")
    ),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `${title.replace(/[^a-z0-9]/gi, "_")}_students.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// ── Status chip helper ────────────────────────────────────────────────────────────
const StatusChip = ({ label, color }) =>
  label && label !== "—" ? (
    <Chip
      label={label}
      size="small"
      sx={{
        bgcolor: `${color}1a`,
        color,
        fontWeight: 600,
        fontSize: "0.72rem",
        border: `1px solid ${color}33`,
        height: 24,
      }}
    />
  ) : (
    <Typography variant="body2" color="text.disabled">—</Typography>
  );

// ── Score badge helper ──────────────────────────────────────────────────────────────
const ScoreBadge = ({ value, isRisk }) => {
  if (value === null || value === undefined)
    return <Typography variant="body2" color="text.disabled">—</Typography>;

  const color =
    value >= 80 ? SUCCESS :
    value >= 60 ? PRIMARY :
    value >= 40 ? WARNING :
                  DANGER;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Box
        sx={{
          px: 1.5, py: 0.25, borderRadius: 2,
          bgcolor: `${color}1a`, color,
          fontWeight: 700, fontSize: "0.8rem",
          display: "inline-flex", alignItems: "center",
        }}
      >
        {value}%
      </Box>
      {isRisk && (
        <Tooltip title="At-risk student (score < 40)">
          <WarningAmberRoundedIcon sx={{ fontSize: 16, color: DANGER }} />
        </Tooltip>
      )}
    </Box>
  );
};

// ── Compute normalised score for a row ───────────────────────────────────────────
const computeScore = (row) => {
  if (row.score !== undefined && row.score !== null) return row.score;
  if (row.solved !== undefined && row.total > 0)
    return Math.round((row.solved / row.total) * 100);
  return null;
};

// ── Derive completion status from row ───────────────────────────────────────────────
const deriveAssignmentStatus = (row, category) => {
  // Prefer explicit field from the enriched API response
  if (row.completionStatus) return row.completionStatus;
  if (category === "completion") {
    if (row.solved !== undefined && row.total !== undefined) {
      if (row.total === 0)              return null;
      if (row.solved === row.total)     return "completed";
      if (row.solved > 0)               return "partial";
      return "notAttempted";
    }
  }
  if (category === "score" && row._score !== undefined) {
    if (row._score === 100) return "completed";
    if (row._score > 0)     return "partial";
    return "notAttempted";
  }
  return null;
};

// ── Main Component ───────────────────────────────────────────────────────────────────────
/**
 * DrillDownPanel
 *
 * @param {string}    title    – Card header title
 * @param {Array}     data     – Student rows from the drill-down API
 * @param {string}    type     – "0-40"|"40-60"|"60-80"|"80-100"
 *                               "completed"|"partial"|"notAttempted"
 *                               "clean"|"suspected"|"highSimilarity"
 * @param {Function}  onClose  – Optional callback to clear the panel
 * @param {boolean}   loading  – Show a loading overlay
 */
const DrillDownPanel = ({
  title,
  data = [],
  type,
  onClose,
  loading = false,
  studentId = null,
  studentName = "",
  leetcodeUsername,
  batchId = null,
}) => {
  const category = getCategory(type);
  
 
  // ── Quick filter state ───────────────────────────────────────────────────────────
  const [scoreFilter, setScoreFilter] = useState("all");
  const [sectionFilter, setSectionFilter] = useState("");
  const [deptFilter, setDeptFilter]     = useState("");

  // ── Solution preview state ───────────────────────────────────────────────────────
  const [previewOpen, setPreviewOpen]             = useState(false);
  const [previewLoading, setPreviewLoading]       = useState(false);
  const [previewSubmission, setPreviewSubmission] = useState(null);
  const [plagiarismReport, setPlagiarismReport]   = useState(null);
  const [peerRows, setPeerRows]                   = useState([]);

  // Per-component submission cache – keyed by "studentId-problemSlug"
  const submissionCache = React.useRef({});

  const handlePreviewOpen = useCallback(async (row) => {
    if (!studentId) return;

    const problemSlug = row.id;  // row.id === p.slug from heatmap API
    const cellKey     = `${studentId}-${problemSlug}`;

    // Seed the panel immediately with what we already know
    const base = {
      studentId,
      problemSlug,
      problemTitle: row.name,
      studentName,
      leetcodeUsername,
      submittedAt:  row.solvedAt ?? null,
      status:       row.solved   ?? false,
      language:     row.language ?? null,
      code:         null,
      runtime:      null,
      memory:       null,
    };

    // Restore from cache if available
    const cached = submissionCache.current[cellKey];
    if (cached) {
      setPreviewSubmission({ ...base, ...cached });
      setPlagiarismReport(cached._plagiarism ?? null);
      setPeerRows(cached._peerPlagiarism    ?? []);
      setPreviewOpen(true);
      return;
    }

    setPreviewSubmission(base);
    setPlagiarismReport(null);
    setPeerRows([]);
    setPreviewLoading(true);
    setPreviewOpen(true);

    try {
      const response = await fetchSubmission(leetcodeUsername, problemSlug);

      // Cache every submission returned in the response
      const allSubs = response.data?.data?.submissions ?? [];
      for (const sub of allSubs) {
        if (!sub?.titleSlug) continue;
        const key = `${studentId}-${sub.titleSlug}`;
        if (!submissionCache.current[key] || sub.code?.trim()) {
          submissionCache.current[key] = sub; 
        }
      }

      const sub         = submissionCache.current[cellKey] ?? {};
      const plagiarism  = response.data?.plagiarism     ?? null;
      const peerPlagiarism = response.data?.peerPlagiarism ?? [];

      // Persist plagiarism alongside the submission in the cache
      if (submissionCache.current[cellKey]) {
        submissionCache.current[cellKey]._plagiarism     = plagiarism;
        submissionCache.current[cellKey]._peerPlagiarism = peerPlagiarism;
      }

      setPreviewSubmission({
        ...base,
        code:     sub.code     ?? null,
        language: sub.language ?? row.language ?? null,
        runtime:  sub.runtime  ?? null,
        memory:   sub.memory   ?? null,
      });
      setPlagiarismReport(plagiarism);
      setPeerRows(peerPlagiarism ?? []);
    } catch (err) {
      console.error('[DrillDownPanel] fetchSubmission error:', err);
    } finally {
      setPreviewLoading(false);
    }
  }, [studentId, studentName]);

  // ── Base rows (add computed _score to each row) ─────────────────────────────────
  const rows = useMemo(
    () =>
      data.map((student, i) => {
        const computed = computeScore(student);
        return {
          // Support both student rows (studentId) and problem rows (slug)
          id: student.studentId || student.slug || String(i),
          ...student,
          _score:  computed,
          _isRisk: computed !== null && computed < 40,
        };
      }),
    [data]
  );

  // ── Filtered rows (quick filters applied client-side) ───────────────────────────
  const filteredRows = useMemo(() => {
    // Problem-detail mode: student-centric filters do not apply
    if (category === "detail") return rows;

    let out = rows;

    // Score range filter
    if (scoreFilter !== "all") {
      out = out.filter((r) => {
        const s = r._score;
        if (s === null) return false;
        if (scoreFilter === "0-40")   return s < 40;
        if (scoreFilter === "40-60")  return s >= 40 && s < 60;
        if (scoreFilter === "60-80")  return s >= 60 && s < 80;
        if (scoreFilter === "80-100") return s >= 80 && s <= 100;
        return true;
      });
    }

    // Section filter (searches row.section if present)
    if (sectionFilter.trim()) {
      const q = sectionFilter.trim().toLowerCase();
      out = out.filter((r) =>
        (r.section || "").toLowerCase().includes(q)
      );
    }

    // Department filter
    if (deptFilter.trim()) {
      const q = deptFilter.trim().toLowerCase();
      out = out.filter((r) =>
        (r.department || "").toLowerCase().includes(q)
      );
    }

    return out;
  }, [rows, scoreFilter, sectionFilter, deptFilter, category]);

  const riskCount = useMemo(
    () => (category === "detail" ? 0 : rows.filter((r) => r._isRisk).length),
    [rows, category]
  );

  // ── Columns ───────────────────────────────────────────────────────────────────────────
  const columns = useMemo(() => {
    // ── Per-problem columns for student-detail mode ──────────────────────────────
    if (category === "detail") {
      const DIFF_COLOR = { Easy: SUCCESS, Medium: WARNING, Hard: DANGER };
      return [
        // 1. Problem Title
        {
          field: "name",
          headerName: "Problem",
          flex: 1,
          minWidth: 180,
          sortable: true,
          renderCell: ({ value, row }) => (
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                {value || "—"}
              </Typography>
              {row.difficulty && (
                <Typography
                  variant="caption"
                  sx={{ color: DIFF_COLOR[row.difficulty] || "text.secondary" }}
                >
                  {row.difficulty}
                </Typography>
              )}
            </Box>
          ),
        },
        // 2. Status
        {
          field: "solved",
          headerName: "Status",
          width: 130,
          sortable: true,
          renderCell: ({ value }) => (
            <StatusChip
              label={value ? "Solved" : "Not Solved"}
              color={value ? SUCCESS : DANGER}
            />
          ),
        },
        // 3. Language
        {
          field: "language",
          headerName: "Language",
          width: 120,
          sortable: true,
          renderCell: ({ value }) =>
            value ? (
              <Chip
                label={value}
                size="small"
                sx={{ fontSize: "0.72rem", height: 24, textTransform: "lowercase" }}
              />
            ) : (
              <Typography variant="body2" color="text.disabled">—</Typography>
            ),
        },
        // 4. Submission Time
        {
          field: "solvedAt",
          headerName: "Submission Time",
          width: 170,
          sortable: true,
          renderCell: ({ value }) =>
            value ? (
              <Typography variant="body2">
                {new Date(value).toLocaleString(undefined, {
                  year: "numeric", month: "short", day: "numeric",
                  hour: "2-digit", minute: "2-digit",
                })}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.disabled">—</Typography>
            ),
        },
        // 5. Plagiarism Status
        {
          field: "plagiarismStatus",
          headerName: "Plagiarism",
          width: 140,
          sortable: true,
          renderCell: ({ value }) =>
            value ? (
              <StatusChip
                label={PLAGIARISM_LABEL[value] || value}
                color={PLAGIARISM_COLOR[value] || INDIGO}
              />
            ) : (
              <Typography variant="body2" color="text.disabled">—</Typography>
            ),
        },
        // 6. Code
        {
          field: "link",
          headerName: "Code",
          width: 110,
          sortable: false,
          renderCell: ({ value, row }) => (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {studentId && row.solved && (
                <Tooltip title="Preview solution">
                  <IconButton
                    size="small"
                    onClick={() => handlePreviewOpen(row)}
                    sx={{ color: INDIGO }}
                  >
                    <VisibilityRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {studentId && !row.solved && value ? (
                <Tooltip title="Open on LeetCode">
                  <IconButton
                    size="small"
                    component="a"
                    href={value}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: PRIMARY }}
                  >
                    <OpenInNewRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                !studentId && <Typography variant="body2" color="text.disabled">—</Typography>
              )}
            </Box>
          ),
        },
      ];
    }

    // ── Default student-list columns ─────────────────────────────────────────
    return [
      // 1. Student Name
      {
        field: "name",
        headerName: "Student Name",
        flex: 1,
        minWidth: 160,
        sortable: true,
        renderCell: ({ value, row }) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            {row._isRisk && (
              <Box
                sx={{
                  width: 6, height: 6, borderRadius: "50%",
                  bgcolor: DANGER, flexShrink: 0,
                }}
              />
            )}
            <Typography variant="body2" fontWeight={600}>
              {value || "—"}
            </Typography>
          </Box>
        ),
      },

      // 2. Roll Number
      {
        field: "rollNumber",
        headerName: "Roll Number",
        width: 140,
        sortable: true,
        renderCell: ({ value }) => (
          <Typography variant="body2" color="text.secondary">{value || "—"}</Typography>
        ),
      },

      // 3. Score
      {
        field: "_score",
        headerName: "Score",
        width: 120,
        type: "number",
        sortable: true,
        renderCell: ({ value, row }) => <ScoreBadge value={value} isRisk={row._isRisk} />,
      },

      // 4. Assignment Status
      {
        field: "_assignmentStatus",
        headerName: "Assignment Status",
        width: 165,
        sortable: true,
        valueGetter: (_value, row) => {
          const key = deriveAssignmentStatus(row, category);
          return key ? COMPLETION_LABEL[key] : "—";
        },
        renderCell: ({ row }) => {
          const key = deriveAssignmentStatus(row, category);
          return (
            <StatusChip
              label={key ? COMPLETION_LABEL[key] : "—"}
              color={key ? COMPLETION_COLOR[key] : INDIGO}
            />
          );
        },
      },

      // 5. Plagiarism Status
      {
        field: "_plagiarismStatus",
        headerName: "Plagiarism Status",
        width: 165,
        sortable: true,
        valueGetter: (_value, row) => {
          const raw = row.plagiarismStatus;
          return raw ? (PLAGIARISM_LABEL[raw] || raw) : "—";
        },
        renderCell: ({ row }) => {
          const raw = row.plagiarismStatus;
          return raw ? (
            <StatusChip
              label={PLAGIARISM_LABEL[raw] || raw}
              color={PLAGIARISM_COLOR[raw] || INDIGO}
            />
          ) : (
            <Typography variant="body2" color="text.disabled">—</Typography>
          );
        },
      },

      // 6. Last Submission
      {
        field: "lastSubmission",
        headerName: "Last Submission",
        width: 155,
        sortable: true,
        valueGetter: (_value, row) => row.lastSubmission || null,
        renderCell: ({ value }) =>
          value ? (
            <Typography variant="body2">
              {new Date(value).toLocaleDateString(undefined, {
                year: "numeric", month: "short", day: "numeric",
              })}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.disabled">—</Typography>
          ),
      },
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, studentId, handlePreviewOpen]);

  return (
    <>
    <GlassCard hoverable={false} sx={{ borderRadius: 4 }}>
      {/* ── Card Header with actions ── */}
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" fontWeight={700}>{title}</Typography>
            {riskCount > 0 && (
              <Chip
                icon={<WarningAmberRoundedIcon sx={{ fontSize: "14px !important" }} />}
                label={`${riskCount} at risk`}
                size="small"
                sx={{
                  bgcolor: `${DANGER}18`, color: DANGER,
                  fontWeight: 700, fontSize: "0.72rem",
                  border: `1px solid ${DANGER}33`,
                }}
              />
            )}
          </Box>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            {category === "detail"
              ? `${filteredRows.length} problem${filteredRows.length !== 1 ? "s" : ""}`
              : `${filteredRows.length} of ${rows.length} student${rows.length !== 1 ? "s" : ""}${
                  filteredRows.length !== rows.length ? " (filtered)" : ""
                }`
            }
          </Typography>
        }
        action={
          <Box sx={{ display: "flex", gap: 0.5, pr: 1, pt: 0.5 }}>
            <Tooltip title="Export filtered list as CSV">
              <span>
                <IconButton
                  size="small"
                  onClick={() => {
                    exportCSV(filteredRows, title);
                    if (batchId) {
                      ctRecordExportActivity(batchId).catch(() => {});
                    }
                  }}
                  disabled={filteredRows.length === 0}
                >
                  <FileDownloadRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {onClose && (
              <Tooltip title="Close drill-down panel">
                <IconButton size="small" onClick={onClose}>
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        }
        sx={{ px: 2.5, pt: 2.5, pb: 0 }}
      />

      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        {/* ── Quick Filters (hidden in student-detail / problem mode) ── */}
        {category !== "detail" && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "center",
            mb: 2,
            p: 1.5,
            borderRadius: 3,
            ...panelMutedSurfaceSx,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <FilterListRoundedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mr: 0.5 }}>
            Filters
          </Typography>

          {/* Score Range */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Score Range</InputLabel>
            <Select
              value={scoreFilter}
              label="Score Range"
              onChange={(e) => setScoreFilter(e.target.value)}
            >
              <MenuItem value="all">All Scores</MenuItem>
              <MenuItem value="0-40">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: DANGER }} />
                  0–40
                </Box>
              </MenuItem>
              <MenuItem value="40-60">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: WARNING }} />
                  40–60
                </Box>
              </MenuItem>
              <MenuItem value="60-80">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: PRIMARY }} />
                  60–80
                </Box>
              </MenuItem>
              <MenuItem value="80-100">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: SUCCESS }} />
                  80–100
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {/* Section */}
          <TextField
            size="small"
            label="Section"
            placeholder="e.g. A"
            value={sectionFilter}
            onChange={(e) => setSectionFilter(e.target.value)}
            sx={{ minWidth: 120 }}
          />

          {/* Department */}
          <TextField
            size="small"
            label="Department"
            placeholder="e.g. CSE"
            value={deptFilter}
            onChange={(e) => setDeptFilter(e.target.value)}
            sx={{ minWidth: 140 }}
          />

          {/* Active filter summary */}
          {(scoreFilter !== "all" || sectionFilter || deptFilter) && (
            <Chip
              label="Clear filters"
              size="small"
              variant="outlined"
              onClick={() => {
                setScoreFilter("all");
                setSectionFilter("");
                setDeptFilter("");
              }}
              sx={{ cursor: "pointer", fontSize: "0.72rem" }}
            />
          )}
        </Box>
        )}

        {/* ── DataGrid ── */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={200}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Box
            sx={{
              // In student-detail mode fit the DataGrid tightly to its rows
              // (rowHeight 56 × rows + header 56 + toolbar/pagination ~168),
              // capped between 240 px and 500 px.
              // In regular student-list mode keep the fixed 500 px.
              height:
                category === "detail"
                  ? Math.max(240, Math.min(filteredRows.length * 56 + 176, 500))
                  : 500,
              width: "100%",
              // Red tinted rows for at-risk students
              "& .drill-row-risk": {
                bgcolor: `${DANGER}0d`,
                "&:hover": { bgcolor: `${DANGER}1a` },
              },
            }}
          >
            <CommonDataGrid
              firstColumnField="name"
              rows={filteredRows}
              columns={columns}
              defaultPageSize={10}
              pageSizeOptions={[10, 25, 50]}
              enableSearch
              searchPlaceholder={category === "detail" ? "Search by problem…" : "Search by name or roll number…"}
              disableRowSelectionOnClick
              rowHeight={56}
              columnHeaderHeight={56}
              getRowClassName={(params) =>
                params.row._isRisk ? "drill-row-risk" : ""
              }
              sx={{
                border: "none",
                "& .MuiDataGrid-cell": { alignItems: "center" },
              }}
            />
          </Box>
        )}
      </CardContent>
    </GlassCard>

      {/* ── Solution Preview Panel ─────────────────────────────────── */}
      <SolutionPreviewPanel
        open={previewOpen}
        onClose={() => { setPreviewOpen(false); setPreviewSubmission(null); }}
        submission={previewSubmission}
        loading={previewLoading}
        plagiarismReport={plagiarismReport}
        peerRows={peerRows}
      />
    </>
  );
};

export default DrillDownPanel;

